/**
 * Settings -> Forms. Admins build forms from a bank of questions; staff send
 * them from a record (a claim today) and the recipient answers by link.
 *
 * Questions are fields on the tenant's `form_request` entity, created with the
 * Entity Editor's shared attribute dialog in its question mode. Which
 * questions a form asks, and when each one shows, lives on the form.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import {
  useDeleteEntityAttribute,
  useEntityTypeByKey,
  useEntityTypes,
  type EntityAttributeDefinition,
  type EntityFieldType,
} from "@/admin/hooks/use-entities";
import {
  useFormTemplates,
  useSaveFormTemplate,
  type FormQuestionRef,
  type FormTemplate,
} from "@/admin/hooks/use-forms";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { EntityAttributeDialog } from "@/admin/pages/manage/entities/entity-editor-page";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, type FormField } from "@/shared/lib/forms";
import { TOAST_DURATION } from "@/shared/lib/toast-duration";

const ENTITY_KEY = "form_request";
// "No show-when rule"; a select item cannot have an empty value.
const ALWAYS = "always";

type Question = EntityAttributeDefinition;
type Option = { value: string; label: string };

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function optionsOf(question: Question | undefined): Option[] {
  return (question?.configuration_data?.options ?? []) as Option[];
}

function typeLabel(types: Option[], fieldType: string): string {
  return (
    types.find((type) => type.value === fieldType)?.label ??
    fieldType.replaceAll("_", " ").toLowerCase()
  );
}

/** "Shown when Survey outcome is Follow up", or null when always shown. */
function ruleText(entry: FormQuestionRef, bank: Question[], t: TFunction): string | null {
  const [parent, answers] = Object.entries(entry.visible_when ?? {})[0] ?? [];
  if (!parent) return null;
  const question = bank.find((q) => q.code_name === parent);
  const labels = (answers ?? []).map(
    (value) => optionsOf(question).find((o) => o.value === value)?.label ?? value,
  );
  return t("forms.settings.shownWhen", "Shown when {{question}} is {{answers}}", {
    question: question?.display_name ?? parent,
    answers: labels.join(", "),
  });
}

/** Takes a question off a form, with any show-when rule that waited on it. */
function withoutEntry(entries: FormQuestionRef[], index: number): FormQuestionRef[] {
  const code = entries[index].code;
  return entries
    .filter((_, i) => i !== index)
    .map((e) => (e.visible_when?.[code] ? { code: e.code, required: e.required } : e));
}

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-[58ch]">
        <h2 className="clara-heading text-[20px]">{title}</h2>
        <p className="mt-1 text-[13px] text-[var(--ink-3)]">{subtitle}</p>
      </div>
      {action}
    </header>
  );
}

function FormQuestionList({
  entries,
  bank,
  answerTypes,
  onAdd,
  onEdit,
  onRemove,
}: {
  entries: FormQuestionRef[];
  bank: Question[];
  answerTypes: Option[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();
  const canAdd = bank.some((q) => !entries.some((e) => e.code === q.code_name));
  // Only the list scrolls; a question just added is scrolled into view.
  const listRef = useRef<HTMLOListElement>(null);
  const count = useRef(entries.length);
  useEffect(() => {
    if (entries.length > count.current) {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
    }
    count.current = entries.length;
  }, [entries.length]);
  return (
    <div className="flex flex-col gap-2" data-testid="form-question-list">
      <Label className="block clara-label font-normal">
        {t("forms.settings.questions", "Questions")}
      </Label>
      {entries.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t("forms.settings.noFormQuestions", "No questions on this form yet.")}
        </p>
      ) : (
        <ol
          ref={listRef}
          className="flex max-h-[40vh] flex-col overflow-y-auto rounded-lg border-[0.5px] border-[var(--rule)]"
        >
          {entries.map((entry, index) => {
            const rule = ruleText(entry, bank, t);
            const question = bank.find((q) => q.code_name === entry.code);
            return (
              <li
                key={entry.code}
                className="flex items-center gap-1 border-b-[0.5px] border-b-[var(--rule)] px-1.5 py-1 last:border-b-0"
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onEdit(index)}
                  className="h-auto flex-1 flex-col items-start gap-0.5 whitespace-normal py-1.5 text-left font-normal"
                  data-testid={`form-question-${entry.code}`}
                >
                  <span className="text-[13px] text-[var(--ink)]">
                    {question?.display_name ?? entry.code}
                  </span>
                  <span className="text-[11px] text-[var(--ink-3)]">
                    {entry.required
                      ? t("forms.settings.required", "Required")
                      : t("forms.settings.optional", "Optional")}
                    {rule ? ` - ${rule}` : ""}
                  </span>
                </Button>
                {question ? (
                  <Badge variant="outline" className="shrink-0 font-normal">
                    {typeLabel(answerTypes, question.field_type)}
                  </Badge>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onRemove(index)}
                  aria-label={t("forms.settings.removeFromForm", "Take off this form")}
                >
                  <X size={14} />
                </Button>
              </li>
            );
          })}
        </ol>
      )}
      {bank.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t("forms.settings.bankEmpty", "Create questions under Questions first.")}
        </p>
      ) : canAdd ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={onAdd}
          data-testid="form-add-question"
        >
          <Plus size={14} />
          {t("forms.settings.addQuestion", "Add question")}
        </Button>
      ) : null}
    </div>
  );
}

/** Adds a question to a form, or edits how one already on it is asked. */
function FormQuestionPicker({
  bank,
  entries,
  index,
  onSave,
  onClose,
}: {
  bank: Question[];
  entries: FormQuestionRef[];
  index: number | null;
  onSave: (entry: FormQuestionRef) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const editing = index === null ? undefined : entries[index];

  const fields = useMemo<FormField[]>(() => {
    const onForm = new Set(entries.map((e) => e.code));
    const choices = bank.filter(
      (q) => q.code_name === editing?.code || !onForm.has(q.code_name),
    );
    // A question can wait on an earlier "pick one" question on this form.
    const earlier = (index === null ? entries : entries.slice(0, index))
      .map((e) => bank.find((q) => q.code_name === e.code))
      .filter((q): q is Question => q?.field_type === "SELECT");
    const [parent, answers] = Object.entries(editing?.visible_when ?? {})[0] ?? [
      ALWAYS,
      [],
    ];
    return [
      {
        type: "select",
        name: "code",
        label: t("forms.settings.colQuestion", "Question"),
        value: editing?.code ?? "",
        required: t("forms.settings.pickQuestionRequired", "Pick a question"),
        options: choices.map((q) => ({ value: q.code_name, label: q.display_name })),
        configurationData: { searchable: true },
        disabled: Boolean(editing),
        testId: "form-question-pick",
      },
      {
        type: "checkbox",
        name: "required",
        label: t("forms.settings.mustAnswer", "Must be answered"),
        value: editing?.required ?? false,
      },
      {
        type: "select",
        name: "parent",
        label: t("forms.settings.showWhen", "Only show when"),
        value: parent,
        options: [
          { value: ALWAYS, label: t("forms.settings.showAlways", "Always show") },
          ...earlier.map((q) => ({ value: q.code_name, label: q.display_name })),
        ],
        visible: earlier.length > 0,
        testId: "form-question-parent",
      },
      ...earlier.map(
        (q): FormField => ({
          type: "multiSelect",
          name: `answers__${q.code_name}`,
          label: t("forms.settings.showAnswers", "Is one of"),
          placeholder: t("forms.settings.showAnswersPlaceholder", "Pick answers"),
          value: q.code_name === parent ? answers : [],
          required: t("forms.settings.showAnswersRequired", "Pick at least one answer"),
          options: optionsOf(q),
          visible: (ctx) => ctx.values.parent === q.code_name,
          testId: `form-question-answers-${q.code_name}`,
        }),
      ),
    ];
  }, [bank, entries, editing, index, t]);

  const { Form, ref } = useForm(
    { name: "form-question-picker", submit: () => <></>, fields },
    async (values) => {
      const entry: FormQuestionRef = {
        code: String(values.code),
        required: Boolean(values.required),
      };
      const parent = String(values.parent ?? ALWAYS);
      if (parent !== ALWAYS) {
        entry.visible_when = { [parent]: values[`answers__${parent}`] as string[] };
      }
      onSave(entry);
    },
    [bank, entries, index, t],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px]" data-testid="form-question-picker">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t("forms.settings.editOnForm", "Question on this form")
              : t("forms.settings.addToForm", "Add a question")}
          </DialogTitle>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            data-testid="form-question-picker-save"
          >
            {editing ? t("common.save", "Save") : t("common.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormTemplateDialog({
  template,
  questions,
  answerTypes,
  onClose,
}: {
  template?: FormTemplate;
  questions: Question[];
  answerTypes: Option[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveFormTemplate();
  // The form's questions as they were when the picker opened.
  const [picker, setPicker] = useState<{
    index: number | null;
    entries: FormQuestionRef[];
  } | null>(null);

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "name",
        label: t("forms.settings.name", "Name"),
        required: t("forms.settings.nameRequired", "Name is required"),
        value: template?.name ?? "",
        placeholder: t("forms.settings.namePlaceholder", "e.g. Site survey"),
      },
      {
        type: "textarea",
        name: "description",
        label: t("forms.settings.description", "Description"),
        value: template?.description ?? "",
      },
      {
        type: "switch",
        name: "is_active",
        label: t("forms.settings.active", "Active"),
        hint: t("forms.settings.activeHint", "Only active forms can be sent."),
        value: template?.is_active ?? true,
      },
      {
        type: "custom",
        name: "questions",
        label: t("forms.settings.questions", "Questions"),
        // A question deleted from the bank since is left off.
        value: (template?.questions ?? []).filter((e) =>
          questions.some((q) => q.code_name === e.code),
        ),
        render: (ctx) => {
          const entries = (ctx.values.questions ?? []) as FormQuestionRef[];
          return (
            <FormQuestionList
              entries={entries}
              bank={questions}
              answerTypes={answerTypes}
              onAdd={() => setPicker({ index: null, entries })}
              onEdit={(index) => setPicker({ index, entries })}
              onRemove={(index) => ctx.setFieldValue("questions", withoutEntry(entries, index))}
            />
          );
        },
      },
    ],
    [template, questions, answerTypes, t],
  );

  const { Form, ref } = useForm(
    { name: "form-template", submit: () => <></>, fields },
    async (values) => {
      const name = String(values.name || "").trim();
      try {
        await save.mutateAsync({
          isNew: !template,
          key: template?.key ?? slugify(name),
          name,
          description: String(values.description || ""),
          is_active: Boolean(values.is_active),
          questions: (values.questions ?? []) as FormQuestionRef[],
        });
        toast.success(t("forms.settings.saved", "Form saved"));
        onClose();
      } catch (err) {
        toastApiError(err, t("forms.settings.saveFailed", "Could not save the form."));
      }
    },
    [template, questions, answerTypes, t],
  );

  const pick = (entry: FormQuestionRef) => {
    if (!picker) return;
    const next = [...picker.entries];
    if (picker.index === null) next.push(entry);
    else next[picker.index] = entry;
    ref.current?.setFieldValue("questions", next);
    setPicker(null);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[560px]" data-testid="form-template-dialog">
        <DialogHeader>
          <DialogTitle>
            {template?.name ?? t("forms.settings.newForm", "New form")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "forms.settings.dialogHint",
              "Add the questions this form asks, in the order they appear.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={save.isPending}
            data-testid="form-template-save"
          >
            {save.isPending
              ? t("forms.settings.saving", "Saving...")
              : t("forms.settings.save", "Save form")}
          </Button>
        </DialogFooter>
        {/* Outside <Form>, so its submit never reaches the form's own. */}
        {picker ? (
          <FormQuestionPicker
            bank={questions}
            entries={picker.entries}
            index={picker.index}
            onSave={pick}
            onClose={() => setPicker(null)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function FormsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can("templates.edit.global") || can("templates.edit.acl");
  const templatesQuery = useFormTemplates();
  const templates = templatesQuery.data ?? [];
  const entityQuery = useEntityTypeByKey(ENTITY_KEY);
  const entity = entityQuery.data;
  const { data: allEntities = [] } = useEntityTypes();
  const deleteQuestion = useDeleteEntityAttribute(entity?.uuid);
  const [editing, setEditing] = useState<FormTemplate | "new" | null>(null);
  const [questionDialog, setQuestionDialog] = useState<{
    open: boolean;
    attribute?: Question;
  }>({ open: false });
  const [deleting, setDeleting] = useState<Question | null>(null);

  // Listing templates creates the question entity on first use; re-check
  // the registry so "New question" appears without a reload.
  const loaded = templatesQuery.isSuccess;
  useEffect(() => {
    if (loaded && !entity) qc.invalidateQueries({ queryKey: ["entity-lookup"] });
  }, [loaded, entity, qc]);

  const questions = useMemo(
    () => (entity?.attributes ?? []).filter((attr) => attr.source === "EAV"),
    [entity],
  );

  // What the recipient's form can render; money, address and record search need lookups it lacks.
  const answerTypes = useMemo<{ value: EntityFieldType; label: string }[]>(
    () => [
      { value: "TEXT", label: t("forms.settings.typeText", "Short text") },
      { value: "RICH_TEXT", label: t("forms.settings.typeLongText", "Long text") },
      { value: "NUMBER", label: t("forms.settings.typeNumber", "Number") },
      { value: "SELECT", label: t("forms.settings.typePickOne", "Pick one") },
      { value: "MULTI_SELECT", label: t("forms.settings.typePickSeveral", "Pick several") },
      { value: "BOOLEAN", label: t("forms.settings.typeTickBox", "Tick box") },
      { value: "DATE", label: t("forms.settings.typeDate", "Date") },
      { value: "DATE_TIME", label: t("forms.settings.typeDateTime", "Date and time") },
      { value: "EMAIL", label: t("forms.settings.typeEmail", "Email") },
      { value: "PHONE", label: t("forms.settings.typePhone", "Phone") },
      { value: "LINK", label: t("forms.settings.typeLink", "Link") },
      { value: "IMAGE", label: t("forms.settings.typePhotos", "Photos") },
    ],
    [t],
  );

  const deleteText = (question: Question) => {
    const total = templates.filter((tpl) =>
      tpl.questions.some((q) => q.code === question.code_name),
    ).length;
    if (total === 0) return t("forms.settings.deleteUnused", "It isn't on any form.");
    if (total === 1) {
      return t(
        "forms.settings.deleteUsedOnce",
        "It's on 1 form. Deleting it takes it off that form.",
      );
    }
    return t(
      "forms.settings.deleteUsedMany",
      "It's on {{total}} forms. Deleting it takes it off them.",
      { total },
    );
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteQuestion.mutateAsync({ attrUuid: deleting.uuid });
      qc.invalidateQueries({ queryKey: ["form-templates"] });
      toast.success(t("forms.settings.deleted", "Question deleted"));
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        toast.error(
          t(
            "forms.settings.deleteHasAnswers",
            "This question has answers on forms already sent, so it can't be deleted.",
          ),
          { duration: TOAST_DURATION.error },
        );
      } else {
        toastApiError(err, t("forms.settings.deleteFailed", "Could not delete the question."));
      }
    }
    setDeleting(null);
  };

  const templateColumns: DataTableColumn<FormTemplate>[] = [
    { id: "name", header: t("forms.settings.colName", "Form"), accessorKey: "name" },
    {
      id: "questions",
      header: t("forms.settings.colQuestions", "Questions"),
      cell: (row) => row.questions.length,
    },
    {
      id: "status",
      header: t("forms.settings.colStatus", "Status"),
      cell: (row) =>
        row.is_active ? (
          <Badge variant="secondary">{t("forms.settings.statusActive", "Active")}</Badge>
        ) : (
          <Badge variant="outline">{t("forms.settings.statusOff", "Off")}</Badge>
        ),
    },
  ];

  const questionColumns: DataTableColumn<Question>[] = [
    {
      id: "question",
      header: t("forms.settings.colQuestion", "Question"),
      accessorKey: "display_name",
    },
    {
      id: "type",
      header: t("forms.settings.colType", "Type"),
      cell: (row) => (
        <Badge variant="outline" className="font-normal">
          {typeLabel(answerTypes, row.field_type)}
        </Badge>
      ),
    },
    ...(canEdit
      ? [
          {
            id: "delete",
            header: "",
            cell: (row: Question) => (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("forms.settings.deleteQuestion", "Delete question")}
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleting(row);
                }}
                data-testid={`forms-delete-question-${row.code_name}`}
              >
                <Trash2 size={14} />
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="pb-16" data-testid="forms-settings">
      <SectionHeader
        title={t("forms.settings.title", "Forms")}
        subtitle={t("forms.settings.subtitle", "Email a form to anyone from a claim.")}
        action={
          canEdit ? (
            <Button onClick={() => setEditing("new")} data-testid="forms-new">
              <Plus size={14} />
              {t("forms.settings.newForm", "New form")}
            </Button>
          ) : null
        }
      />
      <DataTable
        tableId="form-templates"
        columns={templateColumns}
        data={templates}
        isLoading={templatesQuery.isPending}
        isFetching={templatesQuery.isFetching}
        rowKey="uuid"
        onRowClick={canEdit ? (row) => setEditing(row) : undefined}
        emptyMessage={t("forms.settings.emptyForms", "No forms yet.")}
      />

      <Separator className="my-14 bg-[var(--rule-strong)]" />

      <SectionHeader
        title={t("forms.settings.questions", "Questions")}
        subtitle={t(
          "forms.settings.questionsHint",
          "One bank of questions for every form. A question can be used on several forms.",
        )}
        action={
          canEdit && entity ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestionDialog({ open: true })}
              data-testid="forms-add-question"
            >
              <Plus size={14} />
              {t("forms.settings.newQuestion", "New question")}
            </Button>
          ) : null
        }
      />
      <DataTable
        tableId="form-questions"
        columns={questionColumns}
        data={questions}
        isLoading={entityQuery.isConfigLoading || !entity}
        isFetching={entityQuery.isFetching}
        rowKey="uuid"
        onRowClick={canEdit ? (attr) => setQuestionDialog({ open: true, attribute: attr }) : undefined}
        emptyMessage={t("forms.settings.emptyQuestions", "No questions yet.")}
      />

      {editing ? (
        <FormTemplateDialog
          template={editing === "new" ? undefined : editing}
          questions={questions}
          answerTypes={answerTypes}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {entity ? (
        <EntityAttributeDialog
          entity={{
            ...entity,
            relationships: entity.relationships ?? [],
            attributes: entity.attributes ?? [],
          }}
          allEntities={allEntities.length ? allEntities : [entity]}
          attribute={questionDialog.attribute}
          open={questionDialog.open}
          onOpenChange={(open) => setQuestionDialog((s) => ({ ...s, open }))}
          defaultOrder={questions.length}
          defaults={{ source: "EAV" }}
          answerTypes={answerTypes}
        />
      ) : null}
      <ConfirmationDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("forms.settings.deleteTitle", "Delete {{name}}?", {
          name: deleting?.display_name ?? "",
        })}
        description={deleting ? deleteText(deleting) : undefined}
        confirmLabel={t("common.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        pendingLabel={t("forms.settings.deleting", "Deleting...")}
        destructive
        isPending={deleteQuestion.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default FormsTab;
